import React from 'react';
import toast from 'react-hot-toast';
import { AlertCircle, CheckCircle, Download, XCircle } from 'lucide-react';
import importJobService, { type ImportJob } from '../../../services/importJobService';
import { useImportJob } from '../hooks/useActivationJob';
import { getErrorRowInfo } from '../utils';

interface ActivationJobPanelProps {
  jobId: string;
}

const download = async (run: () => Promise<void>) => {
  try {
    await run();
  } catch (error) {
    console.error('Download failed:', error);
    toast.error('Không tải được file. Vui lòng thử lại.');
  }
};

const DownloadButtons: React.FC<{ job: ImportJob }> = ({ job }) => {
  if (job.error_rows === 0 && job.created_accounts === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {job.error_rows > 0 && (
        <button
          onClick={() => download(() => importJobService.downloadReport(job.id))}
          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <Download className="h-4 w-4 mr-2" />
          Tải danh sách dòng lỗi
        </button>
      )}
      {job.created_accounts > 0 && (
        <button
          onClick={() => download(() => importJobService.downloadAccounts(job.id))}
          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <Download className="h-4 w-4 mr-2" />
          Tải tài khoản mới (mật khẩu mặc định)
        </button>
      )}
    </div>
  );
};

const ActivationJobPanel: React.FC<ActivationJobPanelProps> = ({ jobId }) => {
  const { data: job, isError } = useImportJob(jobId);

  // Một lần hỏi lỗi giữa chừng vẫn giữ dữ liệu cũ và lần sau tự hồi, nên chỉ báo lỗi khi chưa có gì
  if (!job) {
    if (isError) {
      return (
        <div className="mt-4 p-4 bg-red-50 rounded-md text-sm text-red-800">
          Không xem được tiến độ lần kích hoạt này. Vui lòng tải lại trang.
        </div>
      );
    }

    return (
      <div className="mt-4 p-4 bg-yellow-50 rounded-md text-sm text-yellow-800">Đang lấy tiến độ...</div>
    );
  }

  if (job.status === 'failed') {
    return (
      <div className="mt-4 p-4 bg-red-50 rounded-md">
        <div className="flex items-start">
          <XCircle className="h-5 w-5 text-red-400 mt-0.5" />
          <div className="ml-3 text-sm">
            <p className="font-medium text-red-800">{job.message}</p>
            {/* total_rows chỉ có khi job đã đọc xong file và bắt đầu chạy; bằng 0 là file bị từ chối
                ngay (quá giới hạn, không đọc được), upload lại nguyên file đó vẫn bị từ chối */}
            <p className="text-red-700 mt-1">
              {job.total_rows > 0
                ? 'Upload lại đúng file này để kích hoạt nốt, các dòng đã xong không bị ảnh hưởng.'
                : 'Sửa file theo thông báo trên rồi upload lại. Chưa có dòng nào được kích hoạt.'}
            </p>
            {/* Lần upload lại tìm thấy các tài khoản đã tạo nên không liệt kê lại: danh sách chỉ còn ở đây */}
            <DownloadButtons job={job} />
          </div>
        </div>
      </div>
    );
  }

  if (job.status !== 'completed') {
    return (
      <div className="mt-4 p-4 bg-blue-50 rounded-md">
        {job.queue_stuck ? (
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
            <p className="ml-3 text-sm font-medium text-yellow-800">{job.message}</p>
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-blue-800">
              Đang kích hoạt {job.processed_rows}/{job.total_rows} dòng
            </p>
            <div className="mt-2 h-2 bg-blue-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={job.progress} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${job.progress}%` }} />
            </div>
            <p className="text-xs text-blue-700 mt-2">Có thể rời trang, quay lại vẫn xem tiếp được.</p>
          </>
        )}
      </div>
    );
  }

  const errors = job.errors ?? [];

  return (
    <div className="mt-4 p-4 bg-green-50 rounded-md">
      <div className="flex items-start">
        <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium text-green-800">
            Hoàn tất: {job.success_rows} thành công, {job.error_rows} lỗi, {job.created_accounts} tài khoản mới
          </p>

          <DownloadButtons job={job} />

          {errors.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-red-700 mb-2">
                {job.error_rows > errors.length ? `${errors.length} lỗi đầu tiên (tải file để xem đủ ${job.error_rows} lỗi):` : 'Các dòng lỗi:'}
              </p>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {errors.map((error) => {
                  const { userName, activationCode } = getErrorRowInfo(error.data);
                  return (
                    <div key={error.row} className="bg-red-50 p-3 rounded border border-red-200">
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-xs font-medium text-red-600">Dòng {error.row}</span>
                        <span className="text-xs text-red-500">{userName}</span>
                      </div>
                      <p className="text-sm text-red-700">{error.error}</p>
                      {activationCode && (
                        <p className="text-xs text-red-600 mt-1">
                          Mã: <code className="bg-red-100 px-1 rounded">{activationCode}</code>
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivationJobPanel;
